import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { locationCoordinates, locationTime } from './LocationDetails';

const addressText = value => typeof value === 'string' ? value.trim() : '';
const locationRipple = keyframes`
    from { background-position: 0% 50%; }
    to { background-position: 100% 50%; }
`;

function LocationTile({ title, icon, children }) {
    return <Box component="section" aria-label={title} sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', minWidth: 0, overflowWrap: 'anywhere' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Box>
        {children}
    </Box>;
}

export function HomeAddressTile({ homeAddress, location, isUsingHomeLocation }) {
    const savedAddress = addressText(homeAddress) || (isUsingHomeLocation ? addressText(location?.address) : '');
    return <LocationTile title="Home Address" icon={<HomeOutlinedIcon />}>
        <Typography>{savedAddress || 'Home address unavailable'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Saved home location</Typography>
    </LocationTile>;
}

export default function LocationTiles({ location, isUsingHomeLocation, homeAddress, homeLat, homeLng, showHome = true }) {
    // A fallback map coordinate is a home location, not a device position report.
    const savedAddress = addressText(homeAddress) || (isUsingHomeLocation ? addressText(location?.address) : '');
    const hasHome = Boolean(savedAddress || isUsingHomeLocation || locationCoordinates({ Latitude: homeLat, Longitude: homeLng }));
    const currentAddress = !isUsingHomeLocation ? addressText(location?.address) : '';
    const hasCurrent = !isUsingHomeLocation && Boolean(currentAddress || locationCoordinates(location));
    const coordinates = locationCoordinates(location);
    const report = hasCurrent ? JSON.stringify([coordinates?.lat, coordinates?.lng, location?.EventTime]) : null;
    const previousReport = useRef(report);
    const [updated, setUpdated] = useState(false);
    useEffect(() => {
        const changed = previousReport.current !== report;
        previousReport.current = report;
        setUpdated(Boolean(report && changed));
        if (report && changed) {
            const timer = setTimeout(() => setUpdated(false), 4000);
            return () => clearTimeout(timer);
        }
    }, [report]);
    const updateSx = updated ? theme => ({
        backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.success.main}, ${theme.palette.primary.main})`,
        backgroundSize: '300% 100%', backgroundClip: 'text', WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent', animation: `${locationRipple} 4s linear`,
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none', backgroundImage: 'none', WebkitTextFillColor: 'currentColor', color: 'primary.main'
        }
    }) : {};

    return <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 15rem), 1fr))', gap: 1.5 }}>
        {showHome && hasHome && <HomeAddressTile homeAddress={homeAddress} location={location} isUsingHomeLocation={isUsingHomeLocation} />}
        <LocationTile title="Last Known Location" icon={<LocationOnOutlinedIcon />}>
            <Typography sx={updateSx}>{hasCurrent ? currentAddress || 'Location address unavailable' : 'Device location unavailable'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={[{ mt: 1 }, updateSx]}>
                {hasCurrent ? `Last reported: ${locationTime(location?.EventTime)}`
                    : isUsingHomeLocation ? 'The map is showing the saved home location.' : 'No device location has been reported.'}
            </Typography>
            {updated && <Typography role="status" variant="caption" color="primary.main">Location updated</Typography>}
        </LocationTile>
    </Box>;
}
